import { el, inject, create, awaitRedraw } from "@dhx/ts-common/dom";
import { uid, isEmptyObj } from "@dhx/ts-common/core";
import { Layout } from "@dhx/ts-layout";
import { Popup } from "@dhx/ts-popup";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { RadioButton, RadioButtonEvents } from "./radiobutton";
import { baseProps, getFormItemCss, getValidationMessage, isVerify } from "../helper";
import { Label } from "./helper/label";
import {
	ValidationStatus,
	IRadioGroupConfig,
	IRadioGroupEventHandlersMap,
	ItemEvent,
	IRadioGroup,
	ICheckboxGroupProps,
	ICheckboxGroupItemProps,
} from "../types";

export class RadioGroup extends Label implements IRadioGroup {
	public config: IRadioGroupConfig;
	public layout: Layout;
	public events: IEventSystem<ItemEvent, IRadioGroupEventHandlersMap> = new EventSystem();

	protected _handlers: any;
	protected _helper: Popup;

	private _buttons: RadioButton[] = [];
	private _isValid = true;
	private _propsItem = [
		"required",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
		"options",
	];
	private _props = [...baseProps, ...this._propsItem];

	constructor(container, config: IRadioGroupConfig) {
		super(null, config);

		this._initView(config);
		this._initHandlers();

		this.config.value && this._setValue(this.config.value);
		this.clearValidate();

		if (this.config.helpMessage) {
			this._helper = new Popup({
				css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light",
			});
			this._helper.attachHTML(this.config.helpMessage);
		}

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	destructor(): void {
		this.events && this.events.clear();
		this.layout && this.layout.destructor();

		this.events = this._uid = this._propsItem = this._props = this._isValid = this._buttons = null;
		super._destructor();
		this.unmount();
	}
	setProperties(arg?: string | ICheckboxGroupProps, props?: ICheckboxGroupItemProps): void {
		if (
			typeof arg === "undefined" ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		)
			return;

		if (typeof arg === "object" && !isEmptyObj(arg)) {
			for (const key in arg) {
				if (this._props.includes(key)) {
					this.config[key] = arg[key];
				}
			}
			if (arg.hasOwnProperty("options")) {
				this._initView(this.config);
				this._initHandlers();
			}
		}
		if (typeof arg === "string" && props && !isEmptyObj(props)) {
			const radio = this._buttons.find(item => item.config.id === arg);
			radio && radio.setProperties(props);
		}

		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.paint();
	}
	getProperties(id?: string): ICheckboxGroupProps | ICheckboxGroupItemProps {
		if (typeof id === "undefined") {
			const obj = {};
			for (const key in this.config) {
				if (this._props.includes(key)) {
					obj[key] = this.config[key];
				}
			}
			return obj;
		}
		const radio = this._buttons.find(item => item.config.id === id);
		return radio.getProperties();
	}
	getValue(): string {
		this._buttons.forEach(element => {
			if (element.getValue()) {
				this.config.value = element.getValue() || "";
			}
		});
		return this.config.value || "";
	}
	setValue(value: string): void {
		if (value === this.config.value) return;
		this._setValue(value);
		this.events.fire(ItemEvent.change, [value]);
		isVerify(this.config) && this.validate();
	}
	focus(id?: string): void {
		awaitRedraw().then(() => {
			if (!this._buttons.length) {
				return;
			}
			if (!id) {
				return this._buttons[0].focus();
			}
			const radiobutton = this._buttons.find(i => i.config.id === id);
			if (!radiobutton) {
				return;
			}
			return radiobutton.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			if (!this._buttons.length) {
				return;
			}
			this._buttons.forEach(radioButton => radioButton.blur());
		});
	}
	show(): void {
		const { hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [this.getValue()])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [this.getValue()]);
	}
	hide(init?: boolean): void {
		const { hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [this.getValue(), init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [this.getValue(), init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		if (this._buttons.length) {
			this._buttons.forEach(button => {
				button.enable();
			});
		}
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	clear(): void {
		if (this.config.value === "") return;
		this._buttons.forEach(element => {
			element.clear();
		});
		this.config.value = "";
		this.events.fire(ItemEvent.change, [this.config.value]);
	}
	validate(silent = false): boolean {
		if (!silent && !this.events.fire(ItemEvent.beforeValidate, [this.getValue()])) return;

		if (this.config.required) {
			this._isValid = this._buttons.some(element => this.config.required && !!element.config.checked);
		}

		if (!silent) {
			this._buttons.forEach(element => {
				element.config.$validationStatus = this._isValid
					? ValidationStatus.success
					: ValidationStatus.error;
			});
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.events.fire(ItemEvent.afterValidate, [this.getValue(), this._isValid]);
		}

		return this._isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this._buttons.map(element => {
			element.clearValidate();
		});
		this.paint();
	}
	protected _initView(config: IRadioGroupConfig): void {
		if (isEmptyObj(config) || !config.options || isEmptyObj(config.options)) {
			throw new Error("Check the configuration is correct");
		}
		if (this.layout) {
			this.layout.destructor();
		}
		if (this._buttons.length) {
			this._buttons.forEach(button => {
				button.destructor();
			});
			this._buttons = [];
		}
		this.config = {
			type: config.type,
			id: config.id,
			name: config.name,
			disabled: false,
			hidden: false,
			options: {},
			required: false,
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			preMessage: "",
			successMessage: "",
			errorMessage: "",
			width: "content",
			height: "content",
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
		const radioButtonsConfig = this.config.options.rows || this.config.options.cols;

		radioButtonsConfig.map(option => {
			option.id = option.id || uid();
		});

		this.layout = new Layout(null, this.config.options);

		radioButtonsConfig.map(option => {
			const radioButton = new RadioButton(null, {
				...option,
				$disabled: config.disabled,
				$name: config.name,
				$required: config.required,
			});

			this._buttons.push(radioButton);
			this.layout.getCell(option.id).attach(radioButton);

			radioButton.events.on(RadioButtonEvents.change, () => {
				this._buttons.map(button => {
					if (button.config.id !== radioButton.config.id) {
						button.setValue(false);
					}
				});
				this.events.fire(ItemEvent.change, [this.getValue()]);
				isVerify(this.config) && this.validate();
			});
			radioButton.events.on(RadioButtonEvents.focus, (value, id) => {
				this.events.fire(ItemEvent.focus, [value, id]);
			});
			radioButton.events.on(RadioButtonEvents.blur, (value, id) => {
				this.events.fire(ItemEvent.blur, [value, id]);
			});
			radioButton.events.on(RadioButtonEvents.keydown, (event, id) => {
				this.events.fire(ItemEvent.keydown, [event, id]);
			});
		});
	}
	protected _initHandlers() {
		this.events.on(ItemEvent.change, () => {
			this.config.value = this.getValue();
			this.paint();
		});
		this.events.on(ItemEvent.afterValidate, () => {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.paint();
		});
	}
	protected _draw() {
		const { label, labelWidth, helpMessage, required, hidden, name, id } = this.config;
		const visibility = hidden ? " dhx_form-group--hidden" : "";

		return el("fieldset.dhx_form-group-fieldset", {}, [
			el(
				"div.dhx_form-group.dhx_form-group--radio-group",
				{
					class: getFormItemCss(this.config, isVerify(this.config)) + visibility,
					"data-dhx-id": name || id,
				},
				[
					label || labelWidth || helpMessage || required ? [this._drawLabel()] : null,
					el("div.dhx_radio-group--container", {}, [
						this.layout && inject(this.layout.getRootView()),
						required &&
							getValidationMessage(this.config) &&
							el("span.dhx_input__caption", getValidationMessage(this.config)),
					]),
				]
			),
		]);
	}
	private _setValue(value) {
		if (typeof value === "undefined" || this._buttons.findIndex(i => i.config.value === value) === -1)
			return;

		this._buttons.forEach(element => {
			value === element.config.value ? element.setValue(true) : element.setValue(false);
		});
	}
}
